import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Button, CircularProgress, IconButton } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  deleteOthRegistrationSubmission,
  exportOthRegistrationSubmissionsCsv,
  listOthRegistrationSubmissions,
  OthRegistrationSubmissionListResult,
  OthRegistrationSubmission,
} from 'src/api/othRegistration';
import {
  REVIEW360_ADMIN_URL,
  Review360Participants,
  review360Api,
} from 'src/api/review360';
import { MuiModal } from 'src/components/material/MuiModal';

import { EditWelcomeEmailModal } from './EditWelcomeEmailModal';
import styles from './OthTab.module.scss';
import {
  emailsWithout360,
  participantRoundText,
  seatsText,
} from '../Oth360Tab/oth360Text';
import { useTabActive } from '../Tabs/tabActiveContext';
import modalStyles from '../UserInfo/Modals/UnsubscribeFromCircle/UnsubscribeFromCircle.module.scss';

type OthTabProps = {
  circleId: string;
  circleName?: string;
  isLeader: boolean;
  /** The circle is connected to a 360review account: show the 360 column and helpers. */
  review360Enabled?: boolean;
};

type Review360State =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; data: Review360Participants }
  | { kind: 'error' };

const answerToText = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '-';
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (typeof value === 'string' || typeof value === 'number') {
    return String(value);
  }
  return JSON.stringify(value);
};

export const OthTab = ({
  circleId,
  circleName,
  isLeader,
  review360Enabled = false,
}: OthTabProps) => {
  const registrationLink = `${window.location.origin}/oth/registration/${circleId}`;
  const isTabActive = useTabActive();

  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [data, setData] = useState<OthRegistrationSubmissionListResult | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);

  const loadSubmissions = useCallback(async () => {
    if (!isLeader) {
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const result = await listOthRegistrationSubmissions(circleId);
      setData(result);
    } catch (err) {
      setError('Could not load OTH submissions.');
    } finally {
      setIsLoading(false);
    }
  }, [circleId, isLeader]);

  useEffect(() => {
    loadSubmissions();
  }, [loadSubmissions]);

  const copyRegistrationLink = async () => {
    try {
      await navigator.clipboard.writeText(registrationLink);
      toast.success('Registration link copied');
    } catch {
      toast.error('Could not copy link');
    }
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);
      const blob = await exportOthRegistrationSubmissionsCsv(circleId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `oth-registration-${circleId}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch {
      toast.error('Could not export submissions');
    } finally {
      setIsExporting(false);
    }
  };

  const answerFields = useMemo(
    () => data?.schema.sections?.flatMap((s) => s.fields) ?? [],
    [data]
  );

  // 360review status of the participants: asked once, when the tab is opened
  const [review360, setReview360] = useState<Review360State>({ kind: 'idle' });
  const review360Requested = useRef(false);
  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);
  useEffect(() => {
    if (!review360Enabled || !isLeader || !isTabActive) {
      return;
    }
    if (review360Requested.current) {
      return;
    }
    review360Requested.current = true;
    setReview360({ kind: 'loading' });
    review360Api
      .getParticipants(circleId)
      .then((result) => {
        if (isMounted.current) {
          setReview360({ kind: 'ready', data: result });
        }
      })
      .catch(() => {
        if (isMounted.current) {
          setReview360({ kind: 'error' });
        }
      });
  }, [circleId, isLeader, isTabActive, review360Enabled]);

  const review360Results =
    review360.kind === 'ready' ? review360.data.results : null;
  const missingEmails = useMemo(
    () =>
      review360Results && data
        ? emailsWithout360(data.submissions, review360Results)
        : [],
    [data, review360Results]
  );

  const copyEmailsWithout360 = async () => {
    if (missingEmails.length === 0) {
      toast('Every participant has a 360');
      return;
    }
    try {
      await navigator.clipboard.writeText(missingEmails.join('\n'));
      toast.success(
        missingEmails.length === 1
          ? '1 email copied'
          : `${missingEmails.length} emails copied`
      );
    } catch {
      toast.error('Could not copy emails');
    }
  };

  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] =
    useState<OthRegistrationSubmission | null>(null);
  const [isWelcomeEmailModalOpen, setIsWelcomeEmailModalOpen] = useState(false);
  const sampleWelcomeName = data?.submissions[0]?.first_name;

  const confirmDelete = async () => {
    if (!deleteTarget) {
      return;
    }
    const submissionId = deleteTarget.id;
    setDeleteTarget(null);

    try {
      setDeletingId(submissionId);
      await deleteOthRegistrationSubmission(circleId, submissionId);
      setData((prev) =>
        prev
          ? {
              ...prev,
              submissions: prev.submissions.filter(
                (s) => s.id !== submissionId
              ),
            }
          : prev
      );
      toast.success('Registration deleted');
    } catch {
      toast.error('Could not delete registration');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <section className={styles.container}>
      <div className={styles.actions}>
        <Button
          variant="contained"
          startIcon={<ContentCopyIcon />}
          onClick={copyRegistrationLink}
        >
          Copy registration link
        </Button>
        {isLeader && (
          <>
            <Button
              variant="outlined"
              startIcon={<EmailOutlinedIcon />}
              onClick={() => setIsWelcomeEmailModalOpen(true)}
            >
              Edit welcome email
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadSubmissions}
              disabled={isLoading}
            >
              Refresh
            </Button>
            <Button
              variant="outlined"
              startIcon={
                isExporting ? <CircularProgress size={16} /> : <DownloadIcon />
              }
              onClick={handleExport}
              disabled={isExporting}
            >
              Export Excel (CSV)
            </Button>
            {review360Enabled && (
              <Button
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                onClick={copyEmailsWithout360}
                disabled={review360.kind !== 'ready' || !data}
              >
                Copy emails without a 360
              </Button>
            )}
          </>
        )}
      </div>

      {isLeader && review360Enabled && (
        <div className={styles.review360Bar}>
          <span className={styles.review360Seats}>
            {review360.kind === 'ready'
              ? seatsText(review360.data.seats)
              : review360.kind === 'error'
                ? '360 status unavailable'
                : 'Loading 360 status...'}
          </span>
          <a
            className={styles.review360AdminLink}
            href={REVIEW360_ADMIN_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open 360review admin
          </a>
        </div>
      )}

      {isLeader ? (
        <div className={styles.dashboard}>
          {isLoading && (
            <div className={styles.loading}>
              <CircularProgress size={26} />
            </div>
          )}
          {!isLoading && error && <p className={styles.error}>{error}</p>}
          {!isLoading && !error && (
            <>
              <h3 className={styles.title}>Registration answers</h3>
              {!data || data.submissions.length === 0 ? (
                <p className={styles.empty}>No registrations submitted yet.</p>
              ) : (
                <div className={styles.tableWrap}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th />
                        <th>Submitted</th>
                        <th>Fornavn</th>
                        <th>Etternavn</th>
                        <th>Phone</th>
                        <th>Email</th>
                        {review360Enabled && <th>360</th>}
                        <th>Firm</th>
                        {answerFields.map((field) => (
                          <th key={field.id}>{field.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.submissions.map((submission) => (
                        <tr key={submission.id}>
                          <td>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setDeleteTarget(submission)}
                              disabled={deletingId === submission.id}
                              title="Delete registration"
                            >
                              {deletingId === submission.id ? (
                                <CircularProgress size={16} />
                              ) : (
                                <DeleteOutlineIcon fontSize="small" />
                              )}
                            </IconButton>
                          </td>
                          <td>
                            {new Date(submission.createdAt).toLocaleString()}
                          </td>
                          <td>{submission.first_name}</td>
                          <td>{submission.surname}</td>
                          <td>{submission.phone_number}</td>
                          <td>{submission.email ?? '-'}</td>
                          {review360Enabled && (
                            <td className={styles.review360Cell}>
                              {review360.kind === 'ready'
                                ? participantRoundText(
                                    review360.data.results[submission.user_id]
                                  )
                                : review360.kind === 'error'
                                  ? '-'
                                  : '…'}
                            </td>
                          )}
                          <td>{submission.firm ?? '-'}</td>
                          {answerFields.map((field) => (
                            <td key={`${submission.id}-${field.id}`}>
                              {answerToText(submission.answers?.[field.id])}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      ) : (
        <p className={styles.memberHint}>
          This tab contains OTH registration tools for circle leaders.
        </p>
      )}

      <EditWelcomeEmailModal
        open={isWelcomeEmailModalOpen}
        circleId={circleId}
        circleTitle={circleName}
        sampleName={sampleWelcomeName}
        onClose={() => setIsWelcomeEmailModalOpen(false)}
      />

      <MuiModal
        open={!!deleteTarget}
        setOpen={(open) => {
          if (!open) {
            setDeleteTarget(null);
          }
        }}
        width={400}
      >
        <div className={modalStyles.unsubscribeModal}>
          <p className={modalStyles.confirmText}>
            Are you sure you want to delete the registration for{' '}
            <strong>
              {deleteTarget?.first_name} {deleteTarget?.surname}
            </strong>
            ?
          </p>
          <div className={modalStyles.btns}>
            <button
              className={modalStyles.btnCancel}
              onClick={() => setDeleteTarget(null)}
            >
              Cancel
            </button>
            <button className={modalStyles.btn} onClick={confirmDelete}>
              Yes
            </button>
          </div>
        </div>
      </MuiModal>
    </section>
  );
};
